﻿// src/cockpit4/components/WorkCiteTrackerPanel.tsx
// 作品引用追踪 — 接入 /ai-visibility/cite-details 真实数据

import { useState, useEffect } from "react";
import {
  Search as SearchIcon, Settings2, Download, Plus, Tag, ChevronRight,
  ArrowUpDown, Info,
} from "lucide-react";
import EmptyState, { MonitorFilterBar, SectionHeader } from "./_MonitorEmptyState";

interface Props {
  dateStart: string;
  dateEnd: string;
  onDateChange: (s: string, e: string) => void;
  onAddBrand: () => void;
}

interface CiteData {
  siteStats: { name: string; count: number }[];
  articleStats: { title: string; count: number }[];
  platformStats: { name: string; count: number }[];
  questionStats: { question: string; count: number }[];
  totalRecords: number;
}

const SITE_TYPE_MAP: Record<string, string> = {
  '官网': '企业官网', 'xshetech.com': '企业官网',
  'CSDN': '技术社区', 'blog.csdn.net': '技术社区',
  '知乎': '社交问答', 'zhuanlan.zhihu.com': '社交问答',
  '公众号': '社交媒体', 'mp.weixin.qq.com': '社交媒体',
  '抖音': '短视频', 'douyin.com': '短视频',
  '小红书': '社交电商', 'xiaohongshu.com': '社交电商',
  'B站': '视频平台', 'bilibili.com': '视频平台',
  '百家号': '内容平台', 'baijiahao.baidu.com': '内容平台',
  '头条': '资讯平台', 'toutiao.com': '资讯平台',
};

function getSiteType(name: string): string {
  return SITE_TYPE_MAP[name] || '其他';
}

export default function WorkCiteTrackerPanel({ dateStart, dateEnd, onDateChange, onAddBrand }: Props) {
  const [data, setData] = useState<CiteData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch("/ai-visibility/cite-details?days=7")
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setData({
            siteStats: d.siteStats || [],
            articleStats: d.articleStats || [],
            platformStats: d.platformStats || [],
            questionStats: d.questionStats || [],
            totalRecords: d.totalRecords || 0,
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dateStart, dateEnd]);

  const articleStats = data?.articleStats || [];
  const siteStats = data?.siteStats || [];
  const totalRecords = data?.totalRecords || 0;
  const totalCiteCount = articleStats.reduce((s, x) => s + x.count, 0);
  const citedArticles = articleStats.filter(a => a.count > 0);
  const citedQuestions = (data?.questionStats || []).filter(q => q.count > 0);

  return (
    <div className="min-h-full text-white">
      <div className="px-4 pt-4">
        <h2 className="text-[17px] font-bold text-white flex items-center gap-2">
          <span className="text-[14px]">🎨</span> 作品引用追踪
          <Info size={13} className="text-gray-500" />
        </h2>
      </div>

      <MonitorFilterBar onAddBrand={onAddBrand}
        dateStart={dateStart} dateEnd={dateEnd} onDateChange={onDateChange} />
      <div className="px-4 pb-8">
        <div className="h-[7px] bg-gradient-to-r from-violet-500/30 via-cyan-500/20 to-violet-500/30 rounded-full mb-4" />

        {/* 数据概览 4 KPI */}
        <SectionHeader label="数据概览" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { v: String(articleStats.length), u: "个", sub: "追踪作品数" },
            { v: String(citedArticles.length), u: "个", sub: "引用作品数" },
            { v: String(citedQuestions.length), u: "个", sub: "引用AI问题数" },
            { v: String(totalCiteCount), u: "次", sub: "引用次数" },
          ].map((k) => (
            <div key={k.sub} className="bg-gradient-to-br from-violet-500/10 via-indigo-500/8 to-cyan-500/10 rounded-2xl border border-violet-500/20 py-6 text-center">
              <div className="text-[40px] font-bold tracking-tight text-violet-400 leading-none">
                {k.v}<small className="text-[18px] font-semibold text-gray-500 ml-0.5">{k.u}</small>
              </div>
              <div className="text-[13px] text-gray-500 mt-3">{k.sub}</div>
            </div>
          ))}
        </div>

        {/* 追踪信息 */}
        <SectionHeader label="追踪信息" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* 左：追踪热点引用Top10 */}
          <div className="bg-black/30 rounded-2xl border border-white/[0.08] p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[14px] font-semibold text-gray-200">追踪热点引用Top10</div>
              <button className="text-[12px] text-violet-400 font-medium hover:text-violet-300 flex items-center">
                查看更多 <ChevronRight size={14} />
              </button>
            </div>
            <div className="overflow-hidden rounded-xl border border-slate-100">
              <table className="w-full text-[12.5px] border-collapse">
                <thead>
                  <tr className="bg-black/30 text-gray-400 text-[11.5px] font-semibold">
                    <th className="text-left p-2.5">站点名称</th>
                    <th className="text-left p-2.5">站点类型</th>
                    <th className="text-right p-2.5">引用次数</th>
                    <th className="text-right p-2.5">引用率</th>
                  </tr>
                </thead>
                <tbody>
                  {siteStats.length > 0 ? (
                    siteStats.slice(0, 10).map((s, i) => (
                      <tr key={i} className="border-t border-white/[0.04]">
                        <td className="p-2.5 text-gray-200">{s.name}</td>
                        <td className="p-2.5 text-gray-400">{getSiteType(s.name)}</td>
                        <td className="p-2.5 text-right text-violet-400 font-semibold">{s.count}</td>
                        <td className="p-2.5 text-right text-gray-300">{totalRecords > 0 ? Math.round(s.count / totalRecords * 100) : 0}%</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={4}><EmptyState text="暂无追踪作品" /></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* 右：文章标签引用Top10 */}
          <div className="bg-black/30 rounded-2xl border border-white/[0.08] p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[14px] font-semibold text-gray-200">文章引用排行Top10</div>
              <button className="text-[12px] text-violet-400 font-medium hover:text-violet-300 flex items-center">
                查看更多 <ChevronRight size={14} />
              </button>
            </div>
            <div className="overflow-hidden rounded-xl border border-slate-100">
              <table className="w-full text-[12.5px] border-collapse">
                <thead>
                  <tr className="bg-black/30 text-gray-400 text-[11.5px] font-semibold">
                    <th className="text-left p-2.5">文章标题</th>
                    <th className="text-right p-2.5">引用次数</th>
                    <th className="text-right p-2.5">引用率</th>
                  </tr>
                </thead>
                <tbody>
                  {articleStats.length > 0 ? (
                    articleStats.slice(0, 10).map((a, i) => (
                      <tr key={i} className="border-t border-white/[0.04]">
                        <td className="p-2.5 text-gray-200 max-w-[200px] truncate" title={a.title}>{a.title}</td>
                        <td className="p-2.5 text-right text-violet-400 font-semibold">{a.count}</td>
                        <td className="p-2.5 text-right text-gray-300">{totalRecords > 0 ? Math.round(a.count / totalRecords * 100) : 0}%</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={3}><EmptyState text="暂无作品标签" /></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 作品追踪记录 */}
        <div className="bg-black/30 rounded-2xl border border-white/[0.08] p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="text-[14px] font-semibold text-gray-200">作品追踪记录</div>
            <div className="flex items-center gap-2 flex-wrap">
              <button className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px] font-bold
                                 bg-gradient-to-r from-violet-500 to-cyan-500 text-white shadow hover:from-violet-600 hover:to-cyan-600">
                <Plus size={13} /> 添加作品
              </button>
              <button className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px]
                                 bg-black/30 border border-white/[0.08] text-white font-semibold hover:text-violet-300 hover:border-violet-400/40">
                <Tag size={13} /> 标签设置
              </button>
              <button className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px]
                                 bg-black/30 border border-white/[0.08] text-white font-semibold hover:text-violet-300 hover:border-violet-400/40">
                <Settings2 size={13} /> 列表配置
              </button>
              <button className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px]
                                 bg-black/30 border border-white/[0.08] text-white font-semibold hover:text-violet-300 hover:border-violet-400/40">
                <Download size={13} /> 全部导出
              </button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-100">
            <table className="w-full text-[12.5px] border-collapse">
              <thead>
                <tr className="bg-black/30 text-gray-400 text-[11.5px]">
                  <th className="text-left p-3 rounded-l-lg w-10">
                    <input type="checkbox" className="accent-violet-500 w-3.5 h-3.5" />
                  </th>
                  <th className="text-left p-3">作品标题 <ArrowUpDown size={10} className="inline" /></th>
                  <th className="text-left p-3">发布站点</th>
                  <th className="text-left p-3">站点类型</th>
                  <th className="text-right p-3">引用次数 <ArrowUpDown size={10} className="inline text-violet-400" /></th>
                  <th className="text-right p-3">引用率 <ArrowUpDown size={10} className="inline" /></th>
                  <th className="text-left p-3 rounded-r-lg">引用AI平台</th>
                </tr>
              </thead>
              <tbody>
                {articleStats.length > 0 ? (
                  articleStats.map((a, i) => (
                    <tr key={i} className="border-t border-white/[0.04] hover:bg-white/[0.02]">
                      <td className="p-3"><input type="checkbox" className="accent-violet-500 w-3.5 h-3.5" /></td>
                      <td className="p-3 text-gray-200 max-w-xs truncate" title={a.title}>{a.title}</td>
                      <td className="p-3 text-gray-400">官网</td>
                      <td className="p-3 text-gray-400">企业官网</td>
                      <td className="p-3 text-right text-violet-400 font-semibold">{a.count}</td>
                      <td className="p-3 text-right text-gray-300">{totalRecords > 0 ? Math.round(a.count / totalRecords * 100) : 0}%</td>
                      <td className="p-3 text-gray-400">{a.count > 0 ? '多个平台' : '—'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7}>
                      <EmptyState text={loading ? "加载中..." : "暂无追踪作品，点击「添加作品」开始追踪你的文章被 AI 引用情况。"} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
